import { ISearchTermRequestModel } from '@myinnsworth/models/ISearchRequestModel';
import axios, { AxiosRequestConfig } from 'axios';
import EventEmitter from './EventEmitter';

export type SearchOrderDirection = 'asc' | 'desc';
export const AUTH_TOKEN_KEY = 'MYI-Auth-Token';
export const REFRESH_TOKEN_KEY = 'MYI-Refresh-Token';

type AxiosRequestConfigWithToken = AxiosRequestConfig & {
    token?: string;
}

abstract class HttpServiceBase extends EventEmitter {
    protected _headers: any = {};
    protected abstract get serviceName(): string;

    private _apiBaseUrl: string | null = null;
    protected get apiBaseUrl(): string {
        if(!this._apiBaseUrl) {
            this._apiBaseUrl = `${process.env.REACT_APP_API_BASE_URL!}`;
        }
        return this._apiBaseUrl;
    }

    private execute(config: AxiosRequestConfigWithToken, serviceName: string | null = null): Promise<any> {
        serviceName = serviceName || this.serviceName;
        
        if(process.env.REACT_APP_ENVIRONMENT === 'dev' || process.env.REACT_APP_ENVIRONMENT === 'staging') {
            console.log(`Executing: ${this.apiBaseUrl}/${serviceName}/${config.url}`);
        }

        const authToken = config.token ? config.token : localStorage.getItem(AUTH_TOKEN_KEY);
        let headers = {
            ...this._headers,
            ...config.headers
        };

        if(authToken) {
            headers['Authorization'] = `Bearer ${authToken}`;
        }

        return new Promise<any>((resolve, reject) => {
            axios({
                method: config.method,
                url: `${this.apiBaseUrl}/${serviceName}/${config.url}`,
                data: config.data,
                headers: headers,
                responseType: config.responseType,
                withCredentials: true
            })
            .then(response => {
                resolve(response);
            })
            .catch(error => reject(error));
        });
    }

    protected setHeader(name: string, value: string) {
        if (this._headers === null) this._headers = {};
        this._headers[name] = value;
    }

    protected removeHeader(name: string) {
        if (name in this._headers) {
            delete this._headers[name];
        }
    }

    protected propsToQuery(obj: any) {
        let params = new Array<string>();
        if(obj) {
            for (let [key, value] of Object.entries(obj.extra)) {
                if(value || value === 0) params.push(`${key}=${encodeURIComponent((value as any).toString())}`);
            }
        }
        return params.length === 0 ? '' : params.join('&');
    }

    protected buildSearchUrl(endpoint: string, args: ISearchTermRequestModel) {
        let url: string = `${endpoint}`;
        let params = new Array<string>();
        if(args.pageNo) params.push(`pageNo=${args.pageNo}`);
        if(args.orderBy) params.push(`orderBy=${args.orderBy}`);
        if(args.orderDir) params.push(`orderDir=${args.orderDir}`);
        if(args.maxResults) params.push(`maxResults=${args.maxResults}`);
        if(args.search) params.push(`search=${encodeURIComponent(args.search)}`);
        if(args.extra) params.push(this.propsToQuery(args.extra));

        if(params.length > 0) url += `?${params.join('&')}`;
        return url;
    }

    protected buildSearchUrlExp(endpoint: string, pageNo: number = 1, resultsPerPage: number | null = 20, orderBy: string = 'id', orderDirection: SearchOrderDirection = 'asc', search: string | null = null, extra: any | null = null): string {
        return this.buildSearchUrl(endpoint, {
            pageNo,
            maxResults: resultsPerPage || undefined,
            orderBy,
            orderDir: orderDirection,
            search: search || undefined,
            extra
        });
    }

    public get(endpoint: string, serviceName: string | null = null): Promise<any> {
        return this.typedGet<any>(endpoint, serviceName);
    }
 
    public typedGet<T>(endpoint: string, options: any | null = null, serviceName: string | null = null): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this.execute({
                method: 'get',
                url: endpoint,
                ...options
            }, serviceName)
                .then(response => resolve(response?.data as T))
                .catch(error => reject(error));
        });
    }

    public fileGet(endpoint: string, serviceName: string | null = null): Promise<any> {
        return this.execute({
            method: 'get',
            url: endpoint,
            responseType: 'blob'
        }, serviceName);
    }

    public getExternal(endpoint: string): Promise<any> {
        return axios({
            method: 'get',
            url: endpoint,
            data: null,
        });
    }

    public put(endpoint: string, serviceName: string | null = null): Promise<any> {
        return this.typedPut<any>(endpoint, serviceName);
    }

    public typedPut<T>(endpoint: string, serviceName: string | null = null): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this.execute({
                method: 'put',
                url: endpoint,
            }, serviceName)
                .then(response => { resolve(response?.data as T); })
                .catch(error => { reject(error); });
        });
    }

    public rawPost(endpoint: string, data: any = null, serviceName: string | null = null): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.execute({
                method: 'post',
                url: endpoint,
                data: data
            }, serviceName)
                .then(response => { resolve(response); })
                .catch(error => { reject(error); });
        });
    }

    public post(endpoint: string, data: any = null, serviceName: string | null = null): Promise<any> {
        return this.typedPost<any>(endpoint, data, serviceName);
    }

    public typedPost<T>(endpoint: string, data: any = null, serviceName: string | null = null): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this.execute({
                method: 'post',
                url: endpoint,
                data: data
            }, serviceName)
                .then(response => { resolve(response?.data as T); })
                .catch(error => { reject(error); });
        });
    }

    public formPost(endpoint: string, data: FormData, serviceName: string | null = null): Promise<any> {
        return this.typedFormPost<any>(endpoint, data, serviceName);
    }

    public typedFormPost<T>(endpoint: string, data: FormData, serviceName: string | null = null): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this.execute({
                method: 'post',
                url: endpoint,
                data: data,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            }, serviceName)
                .then(response => { resolve(response.data as T); })
                .catch(error => { reject(error); });
        });
    }

    public delete(endpoint: string, serviceName: string | null = null): Promise<any> {
        return this.typedDelete<any>(endpoint, serviceName);
    }

    public typedDelete<T>(endpoint: string, serviceName: string | null = null): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this.execute({
                method: 'delete',
                url: endpoint,
            }, serviceName)
                .then(response => { resolve(response.data as T); })
                .catch(error => { reject(error); });
        });
    }
}

//Set environment config
axios.defaults.headers.post['Content-Type'] = 'application/json';

export default HttpServiceBase;